import jsQR from 'jsqr';
import type { ImageDataLike } from './types';

export interface QrDecodeResult {
  data: string;
  strategy: string;
}

/**
 * Decode a QR code from a rendered page image.
 *
 * A page render is mostly white space with the QR occupying a small
 * fraction of it; jsQR's internal binarizer is unreliable on such images at
 * some scales (verified empirically against the real sample PDF — full-page
 * decode alone left occasional pages undecodable). We first try the whole
 * image (cheap, and usually sufficient), then fall back to a grid of
 * overlapping crops at increasing granularity. This is intentionally
 * generic (no assumption about *where* on the page the QR sits) so it works
 * across different gallery-platform page layouts, not just this one.
 */
export function decodeQrFromImage(image: ImageDataLike): QrDecodeResult | null {
  const direct = jsQR(image.data, image.width, image.height, { inversionAttempts: 'attemptBoth' });
  if (direct) return { data: direct.data, strategy: 'full' };

  const tileGrids = [
    { cols: 2, rows: 2, overlap: 0.25 },
    { cols: 3, rows: 3, overlap: 0.3 },
    { cols: 4, rows: 4, overlap: 0.35 }
  ];

  for (const grid of tileGrids) {
    const hit = tryTileGrid(image, grid);
    if (hit) return hit;
  }
  return null;
}

function tryTileGrid(
  image: ImageDataLike,
  grid: { cols: number; rows: number; overlap: number }
): QrDecodeResult | null {
  const { width, height, data } = image;
  const tileW = width / grid.cols;
  const tileH = height / grid.rows;
  const stepX = tileW * (1 - grid.overlap);
  const stepY = tileH * (1 - grid.overlap);

  for (let ty = 0; ty + tileH <= height + 1; ty += stepY) {
    for (let tx = 0; tx + tileW <= width + 1; tx += stepX) {
      const x0 = Math.max(0, Math.round(tx));
      const y0 = Math.max(0, Math.round(ty));
      const w = Math.min(Math.round(tileW), width - x0);
      const h = Math.min(Math.round(tileH), height - y0);
      if (w < 50 || h < 50) continue;

      const sub = new Uint8ClampedArray(w * h * 4);
      for (let row = 0; row < h; row++) {
        const srcStart = ((y0 + row) * width + x0) * 4;
        sub.set(data.subarray(srcStart, srcStart + w * 4), row * w * 4);
      }
      const res = jsQR(sub, w, h, { inversionAttempts: 'attemptBoth' });
      if (res) return { data: res.data, strategy: `tile ${grid.cols}x${grid.rows} @ (${x0},${y0})` };
    }
  }
  return null;
}
